'use client';

import { BigNum, QUOTE_PRECISION_EXP } from '@drift-labs/sdk';
import { MarketId } from '@drift/common';
import { useMemo } from 'react';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { useAllVaultStats } from 'src/hooks/query/vaults/derived/useVaultStats';
import { useAllIfData } from 'src/hooks/query/ifData/useAllIfData';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { useSpotMarketsLookup } from 'src/hooks/spotMarkets/useSpotMarketsLookup';
import { getVaultDepositorBalance } from 'src/utils/vault/math';
import { ZERO_QUOTE_BIGNUM } from 'src/constants/math';
import { useVaultDepositorAccounts } from 'src/hooks/query/vaults/base/useVaultDepositorAccounts';
import { useAllAccountsMarginInfo } from 'src/hooks/query/accountTradingData/useMarginInfo';

export default function useTotalAuthorityValue() {
	const accountsMap = useDriftAccountStore((s) => s.accounts);
	const { data: vaultDepositorAccounts } = useVaultDepositorAccounts();
	const { data } = useAllVaultStats();
	const vaultStatsLookup = data.lookup;
	const { data: ifDataLookup } = useAllIfData();
	const getOraclePriceForMarket = useGetOraclePriceForMarket();
	const spotMarkets = useSpotMarketsLookup();

	const accounts = useMemo(
		() => Object.values(accountsMap).filter(Boolean),
		[accountsMap]
	);
	const { data: allMarginInfos } = useAllAccountsMarginInfo();

	const totalValue = useMemo(() => {
		let total = BigNum.zero(QUOTE_PRECISION_EXP);
		let delegatedTotal = BigNum.zero(QUOTE_PRECISION_EXP);

		accounts.forEach((account) => {
			if (account) {
				const marginInfo = allMarginInfos?.[account.userKey];
				const accountValue = BigNum.from(
					marginInfo?.netUsdValue,
					QUOTE_PRECISION_EXP
				);

				if (account.isDelegatedTo) {
					delegatedTotal = delegatedTotal.add(accountValue);
				} else {
					total = total.add(accountValue);
				}
			}
		});

		Object.values(vaultDepositorAccounts ?? {}).forEach((vaultAccount) => {
			const vaultAssetIndex = vaultAccount.vault.spotMarketIndex;
			const vaultAssetPrecision = spotMarkets[vaultAssetIndex]?.precisionExp;
			const vaultStats = vaultStatsLookup[vaultAccount.vault.pubkey.toString()];

			if (!vaultStats || !vaultAssetPrecision) return;

			const vaultBalance = getVaultDepositorBalance(
				vaultAccount.vaultDepositor,
				{
					totalShares: vaultAccount.vault.totalShares,
					profitShare: vaultAccount.vault.profitShare,
				},
				vaultStats.tvlBase,
				vaultAssetPrecision,
				true
			);

			const oraclePrice = getOraclePriceForMarket(
				MarketId.createSpotMarket(vaultAssetIndex)
			);

			const notionalValueQuote = vaultBalance
				.mul(oraclePrice ?? ZERO_QUOTE_BIGNUM)
				.shiftTo(QUOTE_PRECISION_EXP);

			total = total.add(notionalValueQuote);
		});

		Object.values(ifDataLookup).forEach((ifData) => {
			const currentStakeNotionalValue =
				ifData?.userStake.currentStakeNotionalValue;
			if (currentStakeNotionalValue) {
				total = total.add(currentStakeNotionalValue);
			}
		});

		return {
			totalValue: total,
			delegatedTotal: delegatedTotal.gtZero() ? delegatedTotal : undefined,
		};
	}, [
		accounts,
		allMarginInfos,
		vaultDepositorAccounts,
		ifDataLookup,
		vaultStatsLookup,
		getOraclePriceForMarket,
		spotMarkets,
	]);

	return totalValue;
}
